/**
 * V2 brand registry — the single source of truth for white-label carrier
 * theming. Adding a 4th carrier is data-only: append one row here, one token
 * block in v2-tokens.css (+ optional rail CSS), and one placeholder mark in
 * marks.tsx. NO component code changes.
 *
 * The `data-brand` attribute on <html> (set by useApplyTheme) re-skins the V2
 * app via CSS-variable + component overrides. Role decides the brand
 * authoritatively (see roleToBrand); the manual picker is dev/admin-only and
 * is locked for carrier users.
 */

import type { ComponentType } from 'react';

import { Logo } from '../components/Logo';
import { CitizensMark, StateFarmMark } from './marks';
import type { V2Brand, V2Theme } from '../theme/uiStore';

// V2Brand is declared in uiStore (so the store has no import cycle with this
// registry). Re-export it here so callers can keep importing it from the
// branding module. A 4th carrier extends the union there + the BRANDS record.
export type { V2Brand };

/** Registry entry describing one brand's identity + theming constraints. */
export interface BrandDef {
  /** Stable brand id; matches the `data-brand` attribute value. */
  id: V2Brand;
  /** Picker label. */
  label: string;
  /** Sidebar wordmark (primary line). */
  wordmark: string;
  /** Sidebar wordmark subtitle (small line). */
  sub: string;
  /** Placeholder brand mark — same `{size?}` signature as Logo. */
  Logo: ComponentType<{ size?: number }>;
  /** Themes this brand allows; useApplyTheme coerces to a supported one. */
  supportedThemes: V2Theme[];
  /** When true, the accent is brand-owned and the picker disables swatches. */
  locksAccent: boolean;
  /** Preferred theme when the current one is unsupported. */
  defaultTheme?: V2Theme;
  /** Optional explainer shown in the Appearance popover. */
  note?: string;
}

/**
 * The brand registry. Order here is the picker order.
 */
export const BRANDS: Record<V2Brand, BrandDef> = {
  standard: {
    id: 'standard',
    label: 'Standard',
    wordmark: 'AI Estimate Assistant',
    sub: 'Restoration estimating',
    Logo,
    supportedThemes: ['dark', 'light'],
    locksAccent: false,
    defaultTheme: 'dark',
  },
  citizens: {
    id: 'citizens',
    label: 'Citizens',
    wordmark: 'Citizens',
    sub: 'Estimating Accelerator',
    Logo: CitizensMark,
    supportedThemes: ['light'],
    locksAccent: true,
    defaultTheme: 'light',
    note: 'Citizens skin overrides theme & accent with the official palette.',
  },
  statefarm: {
    id: 'statefarm',
    label: 'State Farm',
    wordmark: 'State Farm',
    sub: 'Estimating Accelerator',
    Logo: StateFarmMark,
    supportedThemes: ['light', 'dark'],
    locksAccent: true,
    defaultTheme: 'dark',
    note: 'State Farm skin applies the official red — available in light & dark.',
  },
};

/**
 * Maps an RBAC role id to the brand it forces. A user holding a carrier role
 * is locked onto that carrier's skin. Adding a 4th carrier adds one row.
 */
export const ROLE_TO_BRAND: Record<string, V2Brand> = {
  citizens: 'citizens',
  statefarm: 'statefarm',
};

/**
 * Resolves the authoritative brand from a user's roles.
 *
 * @param roles - The RBAC role ids the user holds.
 * @returns The first matching carrier brand, else 'standard'.
 */
export function roleToBrand(roles: string[]): V2Brand {
  for (const role of roles) {
    const brand = ROLE_TO_BRAND[role];
    if (brand) return brand;
  }
  return 'standard';
}

/**
 * Whether a brand is a carrier skin (i.e. not the standard brand).
 *
 * @param brand - The brand to test.
 * @returns True for any carrier brand.
 */
export function isCarrierBrand(brand: V2Brand): boolean {
  return brand !== 'standard';
}

/** Picker display order for the brand list. */
export const BRAND_ORDER: V2Brand[] = ['standard', 'citizens', 'statefarm'];

/**
 * The brands a user may SEE in the appearance picker. Carrier skins (Citizens /
 * State Farm) are restricted to super admins; everyone else sees Standard plus,
 * if their role forces a carrier skin, that one (so the locked picker still
 * reflects reality). Pure so it can be unit-tested without the popover.
 *
 * @param roles - The RBAC role ids the user holds.
 * @returns The visible brands, in picker order.
 */
export function visibleBrandsForRoles(roles: string[]): V2Brand[] {
  const isSuperAdmin = roles.includes('super_admin');
  const forced = roleToBrand(roles);
  return BRAND_ORDER.filter((b) => b === 'standard' || isSuperAdmin || b === forced);
}
